/**
 * 「议程」 domain helpers: 清单 (plans) + timetable classes + unsubmitted work.
 *
 * The daemon is authoritative. Everything that comes from the wire (`/agenda`,
 * `plan.response`) is untrusted: rows are validated, capped and deduplicated,
 * malformed rows are dropped, and a malformed envelope returns `null` so the
 * page can surface a real error instead of inventing a day.
 *
 * Timestamps are epoch seconds locally; the daemon serialises ISO 8601 strings
 * (storage uses ms) and `optionalTimestamp` normalises both at this boundary.
 * Dates are the device-local calendar day (`YYYY-MM-DD`), matching the daemon's
 * local-time agenda semantics.
 */
import { isoTimestampFromSeconds, parseDatetimeLocal } from './datetime';
import { randomId } from './id';
import {
  cappedRawText,
  cappedText,
  isEntityId,
  isRecord,
  normalizedTimestamp,
  optionalTimestamp,
} from './validation';

export const AGENDA_PLAN_TEXT_MAX_CHARS = 500;
export const AGENDA_PLAN_NOTES_MAX_CHARS = 2000;
export const AGENDA_MAX_PLANS = 500;
export const AGENDA_MAX_CLASSES = 200;
export const AGENDA_MAX_ASSIGNMENTS = 500;

export type PlanStatus = 'active' | 'done';

/** One 清单 row (daemon `PlanRow` serialised as `ApiPlan`). */
export interface Plan {
  id: string;
  text: string;
  notes: string;
  start_at: number | null;
  due_at: number | null;
  expires_at: number | null;
  /** Derived: neither start_at nor due_at is set (persists until done). */
  undated: boolean;
  status: PlanStatus;
  source: string;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
}

/** One timetable session for a local day (Tsinghua period -> wall clock). */
export interface AgendaClass {
  id: string;
  course: string;
  teacher: string;
  wlkcid: string;
  /** ISO weekday 1..7 (Monday = 1). */
  weekday: number;
  start_period: number;
  end_period: number;
  start_time: string;
  end_time: string;
  location: string;
  week_start: number | null;
  week_end: number | null;
  semester: string;
}

/** One stored (unsubmitted) Learn assignment. */
export interface AgendaAssignment {
  id: string;
  course: string;
  title: string;
  due_at: number | null;
  page_url: string;
}

/** One selected day: plans for the day + all undated plans, classes, work. */
export interface AgendaView {
  date: string;
  /** ISO weekday 1..7 of `date`. */
  weekday: number;
  plans: Plan[];
  classes: AgendaClass[];
  assignments: AgendaAssignment[];
}

// ---------------------------------------------------------------------------
// Local calendar dates
// ---------------------------------------------------------------------------

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
const CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/u;
const MIN_AGENDA_YEAR = 1970;
const MAX_AGENDA_YEAR = 2100;

export const AGENDA_WEEKDAY_LABELS: readonly string[] = [
  '周一',
  '周二',
  '周三',
  '周四',
  '周五',
  '周六',
  '周日',
];

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Local calendar date (`YYYY-MM-DD`) for epoch seconds. */
export function localDateString(seconds: number): string {
  const date = new Date(Math.floor(seconds) * 1000);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Today's local date for epoch seconds (`nowSeconds()`). */
export function todayLocalDate(now: number): string {
  return localDateString(now);
}

/** Calendar-valid `YYYY-MM-DD` within the daemon's supported range. */
export function isValidLocalDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = LOCAL_DATE_PATTERN.exec(value);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < MIN_AGENDA_YEAR || year > MAX_AGENDA_YEAR) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** ISO weekday (Monday = 1 … Sunday = 7) of a local calendar date. */
export function weekdayOfLocalDate(date: string): number {
  const [year, month, day] = date.split('-').map((part) => Number(part));
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`invalid local date: ${date}`);
  }
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 0 ? 7 : weekday;
}

/** Shift a local calendar date by whole days (DST-safe via local midnight). */
export function addLocalDays(date: string, delta: number): string {
  const [year, month, day] = date.split('-').map((part) => Number(part));
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    !Number.isInteger(delta) ||
    !isValidLocalDate(date)
  ) {
    throw new Error(`invalid local date: ${date}`);
  }
  const shifted = new Date(year, month - 1, day + delta);
  return `${shifted.getFullYear()}-${pad2(shifted.getMonth() + 1)}-${pad2(shifted.getDate())}`;
}

export interface LocalDayRange {
  /** Local midnight (inclusive), epoch seconds. */
  start: number;
  /** Next local midnight (exclusive), epoch seconds. */
  end: number;
}

/** Local day range for a `YYYY-MM-DD` date, in epoch seconds. */
export function localDayRange(date: string): LocalDayRange {
  if (!isValidLocalDate(date)) throw new Error(`invalid local date: ${date}`);
  const [year, month, day] = date.split('-').map((part) => Number(part));
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`invalid local date: ${date}`);
  }
  const start = new Date(year, month - 1, day).getTime();
  const end = new Date(year, month - 1, day + 1).getTime();
  return { start: Math.floor(start / 1000), end: Math.floor(end / 1000) };
}

/** Epoch seconds for a local calendar date + optional `HH:MM` (default 00:00). */
export function localDateTimeSeconds(date: string, time = '00:00'): number | null {
  const parsed = parseDatetimeLocal(`${date}T${time}`);
  return parsed.ok ? parsed.seconds : null;
}

/** Heading used by the page header (e.g. `2026-10-20 周二`). */
/** Week 1 of 2026-2027 autumn starts on 2026-09-14 (Tsinghua calendar). */
export const CURRENT_SEMESTER_START_DATE = '2026-09-14';

/** 1-based teaching week for a local date, or null outside the current semester. */
export function academicWeekForLocalDate(date: string): number | null {
  if (!isValidLocalDate(date)) return null;
  const [yearText, monthText, dayText] = date.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const start = new Date(2026, 8, 14).getTime();
  const current = new Date(year, month - 1, day).getTime();
  const diff = current - start;
  if (diff < 0) return null;
  const week = Math.floor(diff / (7 * 86_400_000)) + 1;
  return week >= 1 && week <= 30 ? week : null;
}

export function agendaDateHeading(date: string): string {
  if (!isValidLocalDate(date)) return date;
  return `${date} ${AGENDA_WEEKDAY_LABELS[weekdayOfLocalDate(date) - 1] ?? ''}`;
}

// ---------------------------------------------------------------------------
// Parsers (untrusted wire data)
// ---------------------------------------------------------------------------

function planStatusFromUnknown(value: unknown): PlanStatus {
  return value === 'done' || value === 'completed' ? 'done' : 'active';
}

/** Parse one untrusted plan row; invalid rows are dropped by the caller. */
export function planFromUnknown(value: unknown): Plan | null {
  if (!isRecord(value)) return null;
  if (!isEntityId(value.id)) return null;
  if (typeof value.text !== 'string') return null;
  const text = cappedText(value.text, AGENDA_PLAN_TEXT_MAX_CHARS);
  if (text.length === 0) return null;
  if (value.notes !== undefined && value.notes !== null && typeof value.notes !== 'string') {
    return null;
  }
  const notes =
    typeof value.notes === 'string'
      ? cappedRawText(value.notes.trim(), AGENDA_PLAN_NOTES_MAX_CHARS)
      : '';
  const startAt = optionalTimestamp(value.start_at);
  const dueAt = optionalTimestamp(value.due_at);
  const createdAt = normalizedTimestamp(value.created_at);
  const updatedAt = normalizedTimestamp(value.updated_at) || createdAt;
  const source =
    typeof value.source === 'string' && value.source.trim().length > 0
      ? cappedText(value.source, 32)
      : 'user';
  return {
    id: value.id,
    text,
    notes,
    start_at: startAt,
    due_at: dueAt,
    expires_at: optionalTimestamp(value.expires_at),
    // Derive `undated` from the timestamps: the wire boolean is advisory.
    undated: startAt === null && dueAt === null,
    status: planStatusFromUnknown(value.status),
    source,
    created_at: createdAt,
    updated_at: updatedAt,
    completed_at: optionalTimestamp(value.completed_at),
  };
}

/** Parse `{ plans }` (contract) or a bare array; bad rows are dropped. */
export function plansFromUnknown(value: unknown): Plan[] | null {
  const rawList = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.plans)
      ? value.plans
      : null;
  if (rawList === null) return null;
  const plans = new Map<string, Plan>();
  for (const raw of rawList) {
    const plan = planFromUnknown(raw);
    if (plan !== null) plans.set(plan.id, plan);
  }
  return sortAgendaPlans([...plans.values()]).slice(0, AGENDA_MAX_PLANS);
}

/**
 * Parse a plan mutation response: `{ plan }` (HTTP/relay contract), a full
 * `{ plans }` list (delete), or a bare row (defensive).
 */
export function planMutationFromUnknown(data: unknown, id?: string): Plan | null {
  if (isRecord(data)) {
    const direct = planFromUnknown(data.plan);
    if (direct !== null) return direct;
    const list = plansFromUnknown(data);
    if (list !== null) {
      if (id !== undefined) return list.find((plan) => plan.id === id) ?? null;
      return list.length === 1 ? (list[0] ?? null) : null;
    }
  }
  return planFromUnknown(data);
}

/** Delete only succeeds after an explicit `{ ok: true }` acknowledgement. */
export function isPlanDeleteAcknowledged(value: unknown): boolean {
  return isRecord(value) && value.ok === true;
}

function boundedInt(value: unknown, min: number, max: number): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    return null;
  }
  return value;
}

function requiredText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const text = cappedText(value, max);
  return text.length === 0 ? null : text;
}

function optionalText(value: unknown, max: number): string {
  return typeof value === 'string' ? cappedText(value, max) : '';
}

function optionalBoundedInt(value: unknown, min: number, max: number): number | null {
  return boundedInt(value, min, max);
}

function clockText(value: unknown): string | null {
  return typeof value === 'string' && CLOCK_PATTERN.test(value) ? value : null;
}

/** Parse one untrusted timetable row; invalid rows are dropped by the caller. */
export function agendaClassFromUnknown(value: unknown): AgendaClass | null {
  if (!isRecord(value)) return null;
  if (!isEntityId(value.id)) return null;
  const course = requiredText(value.course, 200);
  if (course === null) return null;
  const weekday = boundedInt(value.weekday, 1, 7);
  const startPeriod = boundedInt(value.start_period, 1, 6);
  const endPeriod = boundedInt(value.end_period, 1, 6);
  if (weekday === null || startPeriod === null || endPeriod === null || startPeriod > endPeriod) {
    return null;
  }
  const startTime = clockText(value.start_time);
  const endTime = clockText(value.end_time);
  if (startTime === null || endTime === null) return null;
  return {
    id: value.id,
    course,
    teacher: optionalText(value.teacher, 200),
    wlkcid: optionalText(value.wlkcid, 100),
    weekday,
    start_period: startPeriod,
    end_period: endPeriod,
    start_time: startTime,
    end_time: endTime,
    location: optionalText(value.location, 200),
    week_start: optionalBoundedInt(value.week_start, 1, 100),
    week_end: optionalBoundedInt(value.week_end, 1, 100),
    semester: optionalText(value.semester, 100),
  };
}

/** Parse one untrusted unsubmitted-assignment row. */
export function agendaAssignmentFromUnknown(value: unknown): AgendaAssignment | null {
  if (!isRecord(value)) return null;
  if (!isEntityId(value.id)) return null;
  const course = requiredText(value.course, 200);
  const title = requiredText(value.title, 300);
  if (course === null || title === null) return null;
  return {
    id: value.id,
    course,
    title,
    due_at: optionalTimestamp(value.due_at),
    page_url: typeof value.page_url === 'string' ? cappedText(value.page_url, 1000) : '',
  };
}

/**
 * Parse the remaining unsubmitted-work list from a `plan.response` restore /
 * dismiss acknowledgement: `{ assignments }` (contract) or a bare array.
 * Malformed rows are dropped; `null` means the envelope itself is unusable.
 */
export function assignmentsFromUnknown(value: unknown): AgendaAssignment[] | null {
  const rawList = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.assignments)
      ? value.assignments
      : null;
  if (rawList === null) return null;
  const assignments = new Map<string, AgendaAssignment>();
  for (const raw of rawList) {
    const item = agendaAssignmentFromUnknown(raw);
    if (item !== null) assignments.set(item.id, item);
  }
  return sortAgendaAssignments([...assignments.values()]).slice(0, AGENDA_MAX_ASSIGNMENTS);
}

/** Dismiss/restore only succeeds after an explicit `{ ok: true }` acknowledgement. */
export function isAssignmentAck(value: unknown): boolean {
  return isRecord(value) && value.ok === true;
}

/**
 * Parse one `agenda` response. Returns `null` when the envelope itself is
 * invalid (date/weekday/lists); malformed rows are dropped and duplicate ids
 * collapse (last row wins).
 */
export function agendaFromUnknown(value: unknown): AgendaView | null {
  if (!isRecord(value)) return null;
  const date = value.date;
  if (!isValidLocalDate(date)) return null;
  const weekday = boundedInt(value.weekday, 1, 7);
  if (weekday === null) return null;
  if (
    !Array.isArray(value.plans) ||
    !Array.isArray(value.classes) ||
    !Array.isArray(value.assignments)
  ) {
    return null;
  }
  const plans = new Map<string, Plan>();
  for (const raw of value.plans) {
    const plan = planFromUnknown(raw);
    if (plan !== null) plans.set(plan.id, plan);
  }
  const classes = new Map<string, AgendaClass>();
  for (const raw of value.classes) {
    const item = agendaClassFromUnknown(raw);
    if (item !== null) classes.set(item.id, item);
  }
  const assignments = new Map<string, AgendaAssignment>();
  for (const raw of value.assignments) {
    const item = agendaAssignmentFromUnknown(raw);
    if (item !== null) assignments.set(item.id, item);
  }
  return {
    date,
    weekday,
    plans: sortAgendaPlans([...plans.values()]).slice(0, AGENDA_MAX_PLANS),
    classes: sortAgendaClasses([...classes.values()]).slice(0, AGENDA_MAX_CLASSES),
    assignments: sortAgendaAssignments([...assignments.values()]).slice(0, AGENDA_MAX_ASSIGNMENTS),
  };
}

// ---------------------------------------------------------------------------
// Ordering / day membership
// ---------------------------------------------------------------------------

/** Active first, dated before undated, then anchor time, then newest created. */
export function sortAgendaPlans(plans: readonly Plan[]): Plan[] {
  return [...plans].sort((left, right) => {
    if (left.status !== right.status) return left.status === 'active' ? -1 : 1;
    const leftAnchor = left.start_at ?? left.due_at;
    const rightAnchor = right.start_at ?? right.due_at;
    if (leftAnchor === null && rightAnchor !== null) return 1;
    if (leftAnchor !== null && rightAnchor === null) return -1;
    if (leftAnchor !== null && rightAnchor !== null && leftAnchor !== rightAnchor) {
      return leftAnchor - rightAnchor;
    }
    return right.created_at - left.created_at;
  });
}

/** Timetable order: start time, then end time, then course. */
export function sortAgendaClasses(classes: readonly AgendaClass[]): AgendaClass[] {
  return [...classes].sort((left, right) => {
    if (left.start_time !== right.start_time) return left.start_time < right.start_time ? -1 : 1;
    if (left.end_time !== right.end_time) return left.end_time < right.end_time ? -1 : 1;
    return left.course === right.course
      ? left.id.localeCompare(right.id)
      : left.course < right.course
        ? -1
        : 1;
  });
}

/** Due soonest first (no due date last), then course/title. */
export function sortAgendaAssignments(
  assignments: readonly AgendaAssignment[],
): AgendaAssignment[] {
  return [...assignments].sort((left, right) => {
    if (left.due_at === null && right.due_at !== null) return 1;
    if (left.due_at !== null && right.due_at === null) return -1;
    if (left.due_at !== null && right.due_at !== null && left.due_at !== right.due_at) {
      return left.due_at - right.due_at;
    }
    return left.title === right.title ? 0 : left.title < right.title ? -1 : 1;
  });
}

/** True when the plan belongs on the selected local day (server SQL semantics). */
export function planFallsOnLocalDate(plan: Plan, date: string): boolean {
  if (plan.undated) return true;
  const anchorStart = plan.start_at ?? plan.due_at;
  const anchorEnd = plan.due_at ?? plan.start_at;
  if (anchorStart === null || anchorEnd === null) return true;
  const range = localDayRange(date);
  return anchorStart < range.end && anchorEnd >= range.start;
}

/** Active plans for a day: dated rows overlapping it plus every undated row. */
export function plansForAgendaDate(plans: readonly Plan[], date: string): Plan[] {
  return sortAgendaPlans(
    plans.filter((plan) => {
      if (plan.status !== 'active') return false;
      return planFallsOnLocalDate(plan, date);
    }),
  );
}

/** `HH:MM` of a timestamp when it is not exactly local midnight, else null. */
function clockOf(seconds: number): string | null {
  const date = new Date(Math.floor(seconds) * 1000);
  if (date.getHours() === 0 && date.getMinutes() === 0) return null;
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * Human display for a plan row: `今天 14:30`, `2026-10-21`, `截止 …` or
 * `不设日期`. Date-only rows are stored at local midnight and render without a
 * clock, so the form's empty time never shows up as `00:00`.
 */
export function planWhenLabel(plan: Plan, today = ''): string {
  if (plan.undated) return '不设日期';
  const anchor = plan.start_at ?? plan.due_at;
  if (anchor === null) return '不设日期';
  const dateLabel =
    today.length > 0 && localDateString(anchor) === today ? '今天' : localDateString(anchor);
  const clock = clockOf(anchor);
  const prefix = plan.start_at === null && plan.due_at !== null ? '截止 ' : '';
  return clock === null ? `${prefix}${dateLabel}` : `${prefix}${dateLabel} ${clock}`;
}

// ---------------------------------------------------------------------------
// Add-plan form
// ---------------------------------------------------------------------------

export interface PlanDraft {
  text: string;
  notes: string;
  /** Local start time in epoch seconds; null = undated. */
  start_at: number | null;
  due_at: number | null;
}

export interface PlanFormValues {
  text: string;
  notes: string;
  /** `YYYY-MM-DD` (ignored when `undated`). */
  date: string;
  /** Optional `HH:MM` (ignored when `undated`). */
  time: string;
  undated: boolean;
}

export type PlanFormField = 'text' | 'notes' | 'date' | 'time';
export type PlanFormErrors = Partial<Record<PlanFormField, string>>;

export type PlanFormValidation =
  { ok: true; value: PlanDraft } | { ok: false; errors: PlanFormErrors; message: string };

export function emptyPlanForm(date: string): PlanFormValues {
  return { text: '', notes: '', date, time: '', undated: false };
}

/** Validate the add-plan form and produce the wire-ready draft. */
export function validatePlanForm(values: PlanFormValues, fallbackDate: string): PlanFormValidation {
  const errors: PlanFormErrors = {};
  const text = cappedText(values.text, AGENDA_PLAN_TEXT_MAX_CHARS);
  if (text.length === 0) errors.text = '清单内容不能为空';
  const notes = cappedRawText(values.notes.trim(), AGENDA_PLAN_NOTES_MAX_CHARS);

  let startAt: number | null = null;
  if (!values.undated) {
    const date = values.date.trim().length > 0 ? values.date.trim() : fallbackDate;
    const time = values.time.trim();
    if (!isValidLocalDate(date)) {
      errors.date = '日期格式无效（YYYY-MM-DD）';
    } else if (time.length > 0 && !CLOCK_PATTERN.test(time)) {
      errors.time = '时间格式无效（HH:MM）';
    } else {
      const dateTime = localDateTimeSeconds(date, time.length > 0 ? time : '00:00');
      if (dateTime === null || dateTime <= 0) {
        errors.date = '日期时间无效';
      } else {
        startAt = dateTime;
      }
    }
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors, message: '请检查清单内容后重试' };
  }
  return { ok: true, value: { text, notes, start_at: startAt, due_at: null } };
}

/** `plan.request` create body: snake_case + ISO 8601 timestamps (or null). */
export function planCreateBody(draft: PlanDraft): Record<string, unknown> {
  return {
    text: draft.text,
    notes: draft.notes,
    start_at: isoTimestampFromSeconds(draft.start_at),
    due_at: isoTimestampFromSeconds(draft.due_at),
  };
}

// ---------------------------------------------------------------------------
// Demo data (clearly labelled in the UI; never used in live mode)
// ---------------------------------------------------------------------------

/** Small deterministic mock plan set for demo mode (clearly labelled 演示). */
export function demoAgendaPlans(now: number): Plan[] {
  const today = todayLocalDate(now);
  const tomorrow = addLocalDays(today, 1);
  const make = (
    id: string,
    text: string,
    date: string | null,
    time: string,
    notes: string,
    status: PlanStatus = 'active',
  ): Plan => {
    const startAt = date === null ? null : localDateTimeSeconds(date, time);
    return {
      id,
      text,
      notes,
      start_at: startAt,
      due_at: null,
      expires_at: null,
      undated: startAt === null,
      status,
      source: 'demo',
      created_at: now - 3600,
      updated_at: now - 3600,
      completed_at: status === 'done' ? now - 1800 : null,
    };
  };
  return [
    make('plan_demo_lecture', '去听“模型与智能”讲座', today, '14:30', '演示数据：带时间的清单项。'),
    make('plan_demo_undated', '整理本周计划与材料', null, '', '演示数据：不设日期，一直保留。'),
    make('plan_demo_report', '提交实验报告初稿', tomorrow, '09:00', '演示数据：明天的清单项。'),
    make('plan_demo_done', '预约体检（已完成）', today, '10:00', '演示数据：已完成状态。', 'done'),
  ];
}

/** Small deterministic timetable for demo mode (Mon/Wed/Fri entries). */
export function demoAgendaClasses(): AgendaClass[] {
  return [
    {
      id: 'class_demo_ai',
      course: '人工智能导论',
      teacher: '张老师',
      wlkcid: '',
      weekday: 1,
      start_period: 1,
      end_period: 2,
      start_time: '08:00',
      end_time: '09:35',
      location: '自强科技楼 5312',
      week_start: 1,
      week_end: 16,
      semester: '2026 秋',
    },
    {
      id: 'class_demo_math',
      course: '高等数学',
      teacher: '李老师',
      wlkcid: '',
      weekday: 3,
      start_period: 5,
      end_period: 6,
      start_time: '13:30',
      end_time: '15:05',
      location: '六教 6A201',
      week_start: 1,
      week_end: 16,
      semester: '2026 秋',
    },
    {
      id: 'class_demo_physics',
      course: '大学物理',
      teacher: '王老师',
      wlkcid: '',
      weekday: 5,
      start_period: 3,
      end_period: 4,
      start_time: '09:50',
      end_time: '11:25',
      location: '三教 3201',
      week_start: 1,
      week_end: 16,
      semester: '2026 秋',
    },
  ];
}

/** Small deterministic unsubmitted-work set for demo mode. */
export function demoAgendaAssignments(now: number): AgendaAssignment[] {
  return [
    {
      id: 'assignment_demo_perceptron',
      course: '人工智能导论',
      title: '实验一：感知机',
      due_at: now + 2 * 86_400,
      page_url: '',
    },
    {
      id: 'assignment_demo_math',
      course: '高等数学',
      title: '习题 3.4',
      due_at: now + 4 * 86_400,
      page_url: '',
    },
  ];
}

/**
 * Build the demo agenda for one day from the local demo plan store. Active
 * undated plans always show; dated plans (including a done row, to exercise the
 * done state) show when they belong to the selected day. `dismissedAssignments`
 * is the local demo-only dismissal list (ids the user removed from 议程); live
 * dismissals are persisted by the daemon and never use this list.
 */
export function buildDemoAgenda(
  date: string,
  plans: readonly Plan[],
  now: number,
  dismissedAssignments: readonly string[] = [],
): AgendaView {
  if (!isValidLocalDate(date)) throw new Error(`invalid local date: ${date}`);
  const weekday = weekdayOfLocalDate(date);
  const visible = plans.filter((plan) => {
    if (plan.undated) return plan.status === 'active';
    return planFallsOnLocalDate(plan, date);
  });
  return {
    date,
    weekday,
    plans: sortAgendaPlans(visible),
    classes: sortAgendaClasses(demoAgendaClasses().filter((item) => item.weekday === weekday)),
    assignments: sortAgendaAssignments(
      demoAgendaAssignments(now).filter((item) => !dismissedAssignments.includes(item.id)),
    ),
  };
}

/** Build one local demo plan row from a validated draft. */
export function demoPlanFromDraft(draft: PlanDraft, now: number): Plan {
  return {
    id: randomId('plan_demo_'),
    text: draft.text,
    notes: draft.notes,
    start_at: draft.start_at,
    due_at: draft.due_at,
    expires_at: null,
    undated: draft.start_at === null && draft.due_at === null,
    status: 'active',
    source: 'demo',
    created_at: now,
    updated_at: now,
    completed_at: null,
  };
}

/** Mark a local demo plan done (kept visible on its day to show the state). */
export function demoPlanDone(plans: readonly Plan[], id: string, now: number): Plan[] {
  return sortAgendaPlans(
    plans.map((plan) =>
      plan.id === id
        ? { ...plan, status: 'done' as const, completed_at: now, updated_at: now }
        : plan,
    ),
  );
}

/** Remove a local demo plan. */
export function demoPlanDeleted(plans: readonly Plan[], id: string): Plan[] {
  return plans.filter((plan) => plan.id !== id);
}
